import { useState, useCallback, useEffect } from 'react'
import { GridApi } from 'ag-grid-community'
import store from '../../../../store'
import { addScreenMessage, MessageLevel } from '../../../../store/messages'
import { intl } from '../../../../i18n'
import {
  fetchWbsItemSubStatuses,
  updateWbsItemSubStatuses,
  WbsItemSubStatusResponse,
} from '../../../../infrastructure/api/wbs-item-sub-statuses'
import {
  SubStatusRow,
  createNewSubStatusRow,
  fromResponse,
} from '../SubStatusRow'

// 追加行のうち、名前・紐づくステータスのどちらも未入力の行(自動追加した空行含む)は
// 「まだ何も入力していない」とみなし、変更検知・保存対象から除外する
const isBlankAddedRow = (row: SubStatusRow): boolean =>
  !!row.added && !row.name.trim() && !row.linkedStatus

type UseSubStatusDataProps = {
  wbsItemType: string
  functionUuid: string
  gridApi?: GridApi
}

type UseSubStatusDataReturn = {
  rowData: SubStatusRow[]
  originalData: SubStatusRow[]
  hasUnsavedChanges: boolean
  loading: boolean
  handleReload: () => Promise<void>
  handleSubmit: () => Promise<void>
  handleDataChange: (newData: SubStatusRow[]) => void
}

// 表示順(0始まりのインデックス)を uuid ごとに算出する。
// プリセット・カスタムを問わず全行が並べ替え対象なので、両方を通し番号の対象にする。
const buildDisplayOrderMap = (rows: SubStatusRow[]): Map<string, number> => {
  const map = new Map<string, number>()
  rows
    .filter(row => !isBlankAddedRow(row))
    .forEach((row, index) => {
      map.set(row.uuid, index)
    })
  return map
}

const checkForChanges = (
  current: SubStatusRow[],
  original: SubStatusRow[]
): boolean => {
  const meaningfulCurrent = current.filter(row => !isBlankAddedRow(row))
  if (meaningfulCurrent.length !== original.length) {
    return true
  }
  const currentOrder = buildDisplayOrderMap(current)
  const originalOrder = buildDisplayOrderMap(original)
  return meaningfulCurrent.some(row => {
    const orig = original.find(o => o.uuid === row.uuid)
    if (!orig) return true
    if (row.name !== orig.name || row.linkedStatus !== orig.linkedStatus) {
      return true
    }
    // 並び順の変更も未保存扱いにする
    return currentOrder.get(row.uuid) !== originalOrder.get(row.uuid)
  })
}

/**
 * サブステータスデータの管理とAPI連携を行うフック
 */
export const useSubStatusData = ({
  wbsItemType,
  functionUuid,
  gridApi,
}: UseSubStatusDataProps): UseSubStatusDataReturn => {
  const [rowData, setRowData] = useState<SubStatusRow[]>([])
  const [originalData, setOriginalData] = useState<SubStatusRow[]>([])
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [loading, setLoading] = useState(false)

  const handleDataChange = useCallback(
    (newData: SubStatusRow[]) => {
      setRowData(newData)
      setHasUnsavedChanges(checkForChanges(newData, originalData))
    },
    [originalData]
  )

  const handleReload = useCallback(async () => {
    if (!wbsItemType) {
      setRowData([])
      setOriginalData([])
      setHasUnsavedChanges(false)
      return
    }
    setLoading(true)
    try {
      const response = await fetchWbsItemSubStatuses(wbsItemType)
      const items = (response.json ?? []) as WbsItemSubStatusResponse[]
      const rows = items.map(fromResponse)
      // 1件もない場合は、チーム登録画面などと同様に空行を1つ見える状態にしておく
      setRowData(rows.length > 0 ? rows : [createNewSubStatusRow()])
      setOriginalData(rows.map(row => ({ ...row })))
      setHasUnsavedChanges(false)
    } finally {
      setLoading(false)
    }
  }, [wbsItemType])

  const handleSubmit = useCallback(async () => {
    if (!wbsItemType || !hasUnsavedChanges) return

    gridApi?.stopEditing()

    const currentOrder = buildDisplayOrderMap(rowData)
    const originalOrder = buildDisplayOrderMap(originalData)

    // 内容(名前/ステータス)変更に加え、並び順が変わった行も編集対象に含める。
    // 1行動かすと後続の行のインデックスもずれるため、それらも displayOrder を更新する必要がある。
    const editedRows = rowData.filter(row => {
      if (row.added) return false
      const orig = originalData.find(o => o.uuid === row.uuid)
      if (!orig) return false
      const contentChanged =
        row.name !== orig.name || row.linkedStatus !== orig.linkedStatus
      const orderChanged =
        currentOrder.get(row.uuid) !== originalOrder.get(row.uuid)
      return contentChanged || orderChanged
    })

    // 追加行・編集行いずれも、名前・紐づくステータスは必須(バックエンドの@NotEmptyと揃える)
    const invalidRows = [
      ...rowData.filter(
        row =>
          row.added &&
          !isBlankAddedRow(row) &&
          (!row.name.trim() || !row.linkedStatus)
      ),
      ...editedRows.filter(row => !row.name.trim() || !row.linkedStatus),
    ]
    if (invalidRows.length > 0) {
      store.dispatch(
        addScreenMessage(functionUuid, {
          type: MessageLevel.WARN,
          title: intl.formatMessage({
            id: 'subStatus.validation.required',
          }),
        })
      )
      return
    }

    // 同名のサブステータスは区別がつかず紛らわしいため、重複したまま保存させない
    const names = rowData
      .filter(row => !isBlankAddedRow(row))
      .map(row => row.name.trim().toLowerCase())
      .filter(Boolean)
    const hasDuplicateName = names.length !== new Set(names).size
    if (hasDuplicateName) {
      store.dispatch(
        addScreenMessage(functionUuid, {
          type: MessageLevel.WARN,
          title: intl.formatMessage({
            id: 'subStatus.validation.duplicate',
          }),
        })
      )
      return
    }

    const added = rowData
      .filter(row => row.added && !isBlankAddedRow(row))
      .map(row => ({
        name: row.name.trim(),
        linkedStatus: row.linkedStatus,
        displayOrder: currentOrder.get(row.uuid) ?? 0,
      }))

    const edited = editedRows.map(row => ({
      uuid: row.uuid,
      name: row.name.trim(),
      linkedStatus: row.linkedStatus,
      displayOrder: currentOrder.get(row.uuid) ?? 0,
    }))

    // プリセットかどうかによらず削除できる。
    // 使用中(WBSアイテムに設定済み)の行は削除操作の時点で除外済みで、バックエンドでも二重にガードしている。
    const deleted = originalData
      .filter(orig => !rowData.some(row => row.uuid === orig.uuid))
      .map(orig => ({ uuid: orig.uuid }))

    if (added.length === 0 && edited.length === 0 && deleted.length === 0) {
      setHasUnsavedChanges(false)
      return
    }

    setLoading(true)
    try {
      await updateWbsItemSubStatuses({ wbsItemType, added, edited, deleted })
      store.dispatch(
        addScreenMessage(functionUuid, {
          type: MessageLevel.SUCCESS,
          title: intl.formatMessage({ id: 'registration.complete' }),
        })
      )
      await handleReload()
    } catch {
      store.dispatch(
        addScreenMessage(functionUuid, {
          type: MessageLevel.ERROR,
          title: intl.formatMessage({ id: 'subStatus.error.save' }),
        })
      )
    } finally {
      setLoading(false)
    }
  }, [
    wbsItemType,
    hasUnsavedChanges,
    rowData,
    originalData,
    functionUuid,
    gridApi,
    handleReload,
  ])

  useEffect(() => {
    handleReload()
  }, [handleReload])

  return {
    rowData,
    originalData,
    hasUnsavedChanges,
    loading,
    handleReload,
    handleSubmit,
    handleDataChange,
  }
}
